/**
 * Demo mode anonymization utilities.
 *
 * Provides deterministic name anonymization for demo presentations.
 * Same input always produces same output for consistency across the app.
 * Mirrors the backend common/anonymize.py logic.
 */

// Fake owner/organization names (150 to minimize hash collisions)
const FAKE_OWNER_NAMES = [
  "Apex Healthcare Group",
  "Summit Care Management",
  "Pinnacle Health Services",
  "Horizon Senior Holdings",
  "Cornerstone Care Partners",
  "Meridian Health Group",
  "Premier Care Solutions",
  "Keystone Healthcare",
  "Landmark Senior Services",
  "Atlas Care Management",
  "Beacon Health Partners",
  "Sterling Care Group",
  "Vista Healthcare Holdings",
  "Cardinal Senior Services",
  "Frontier Care Management",
  "Pacific Health Partners",
  "Continental Care Group",
  "National Senior Holdings",
  "United Care Services",
  "Alliance Health Management",
  "Sunrise Senior Living",
  "Golden Age Healthcare",
  "Evergreen Care Partners",
  "Oakwood Health Services",
  "Riverside Senior Holdings",
  "Harmony Care Management",
  "Crestview Health Group",
  "Liberty Healthcare Partners",
  "Northstar Senior Services",
  "Silverado Care Solutions",
  "Brookside Health Holdings",
  "Heartland Care Group",
  "Lakewood Senior Partners",
  "Highland Health Services",
  "Coastal Care Management",
  "Valley Health Holdings",
  "Mountainview Care Partners",
  "Clearwater Senior Services",
  "Bridgeport Healthcare Group",
  "Westfield Care Solutions",
  "Eastgate Health Partners",
  "Parkview Senior Holdings",
  "Grandview Care Management",
  "Fairfield Health Services",
  "Springdale Senior Partners",
  "Cedarwood Care Group",
  "Maplewood Health Holdings",
  "Willowbrook Senior Services",
  "Pinecrest Care Solutions",
  "Ashford Health Partners",
  "Stonegate Senior Holdings",
  "Ridgewood Care Management",
  "Greenfield Health Services",
  "Bayshore Senior Partners",
  "Southfield Care Group",
  "Woodlands Health Holdings",
  "Suncoast Senior Services",
  "Northfield Care Solutions",
  "Heritage Health Partners",
  "Providence Senior Holdings",
  "Elmwood Care Associates",
  "Birchwood Health Systems",
  "Cypress Senior Partners",
  "Juniper Care Holdings",
  "Sequoia Health Management",
  "Redwood Senior Services",
  "Magnolia Care Group",
  "Dogwood Health Partners",
  "Hickory Senior Holdings",
  "Chestnut Care Solutions",
  "Aspen Health Associates",
  "Spruce Senior Management",
  "Laurel Care Partners",
  "Hawthorn Health Group",
  "Sycamore Senior Services",
  "Acadia Healthcare Holdings",
  "Cascade Care Management",
  "Olympic Health Partners",
  "Sierra Senior Solutions",
  "Rocky Mountain Care Group",
  "Blue Ridge Health Holdings",
  "Appalachian Senior Services",
  "Great Lakes Care Partners",
  "Prairie Health Management",
  "Desert Sun Senior Holdings",
  "Tidewater Care Solutions",
  "Gulf Coast Health Group",
  "Atlantic Senior Partners",
  "Chesapeake Care Holdings",
  "Hudson Valley Health Services",
  "New England Senior Management",
  "Midwest Care Associates",
  "Southwest Health Partners",
  "Northwest Senior Solutions",
  "Tri-State Care Holdings",
  "Capital Health Management",
  "Empire Senior Services",
  "Liberty Bell Care Group",
  "Patriot Health Partners",
  "Freedom Senior Holdings",
  "Independence Care Solutions",
  "Unity Health Associates",
  "Integrity Senior Management",
  "Excellence Care Partners",
  "Quality Health Holdings",
  "Prestige Senior Services",
  "Elite Care Management",
  "Select Health Partners",
  "Choice Senior Solutions",
  "Preferred Care Holdings",
  "Signature Health Group",
  "Legacy Senior Associates",
  "Traditions Care Partners",
  "Generations Health Holdings",
  "Family First Senior Services",
  "Caring Hands Management",
  "Gentle Touch Care Group",
  "Compassionate Health Partners",
  "Devoted Senior Holdings",
  "Dedicated Care Solutions",
  "Committed Health Associates",
  "Trusted Senior Management",
  "Reliable Care Partners",
  "Dependable Health Holdings",
  "Steadfast Senior Services",
  "Faithful Care Group",
  "Loyal Health Partners",
  "True North Senior Holdings",
  "Guiding Light Care Solutions",
  "Pathways Health Associates",
  "Journey Senior Management",
  "Milestone Care Partners",
  "Benchmark Health Holdings",
  "Standard Senior Services",
  "Criterion Care Group",
  "Paradigm Health Partners",
  "Vanguard Senior Holdings",
  "Pioneer Care Solutions",
  "Trailblazer Health Associates",
  "Navigator Senior Management",
  "Compass Care Partners",
  "Anchor Health Holdings",
  "Harbor Senior Services",
  "Lighthouse Care Group",
  "Beacon Bay Health Partners",
  "Safe Harbor Senior Holdings",
];

/**
 * Simple hash function to convert string to number.
 * Uses the same algorithm as Python's hashlib.md5 but simplified.
 */
function hashToIndex(value: string, listLength: number): number {
  if (!value) return 0;

  // Simple hash - sum of char codes with position weighting
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash) % listLength;
}

/**
 * Convert a real owner/organization name to a fake one.
 * Deterministic - same input always gives same output.
 */
export function anonymizeOwnerName(realName: string | null | undefined): string {
  if (!realName) return realName || '';
  const idx = hashToIndex(realName, FAKE_OWNER_NAMES.length);
  return FAKE_OWNER_NAMES[idx];
}

// Fake cities for anonymization
const FAKE_CITIES = [
  "Springfield", "Riverside", "Fairview", "Madison", "Georgetown",
  "Clinton", "Franklin", "Greenville", "Bristol", "Oakland",
  "Salem", "Burlington", "Manchester", "Milton", "Newport",
  "Arlington", "Ashland", "Clayton", "Dover", "Hudson"
];

// Fake states with corresponding districts
const STATE_DISTRICT_MAP: { [key: string]: string[] } = {
  "CA": ["N.D. Cal.", "C.D. Cal.", "S.D. Cal.", "E.D. Cal."],
  "TX": ["N.D. Tex.", "S.D. Tex.", "E.D. Tex.", "W.D. Tex."],
  "FL": ["N.D. Fla.", "M.D. Fla.", "S.D. Fla."],
  "NY": ["E.D.N.Y.", "S.D.N.Y.", "N.D.N.Y.", "W.D.N.Y."],
  "IL": ["N.D. Ill.", "C.D. Ill.", "S.D. Ill."],
  "PA": ["E.D. Pa.", "M.D. Pa.", "W.D. Pa."],
  "OH": ["N.D. Ohio", "S.D. Ohio"],
  "GA": ["N.D. Ga.", "M.D. Ga.", "S.D. Ga."],
  "NC": ["E.D.N.C.", "M.D.N.C.", "W.D.N.C."],
  "MI": ["E.D. Mich.", "W.D. Mich."],
  "NJ": ["D.N.J."],
  "VA": ["E.D. Va.", "W.D. Va."],
  "WA": ["E.D. Wash.", "W.D. Wash."],
  "AZ": ["D. Ariz."],
  "MA": ["D. Mass."],
  "TN": ["E.D. Tenn.", "M.D. Tenn.", "W.D. Tenn."],
  "IN": ["N.D. Ind.", "S.D. Ind."],
  "MO": ["E.D. Mo.", "W.D. Mo."],
  "MD": ["D. Md."],
  "WI": ["E.D. Wis.", "W.D. Wis."]
};

const FAKE_STATES = Object.keys(STATE_DISTRICT_MAP);

/**
 * Convert a real city name to a fake one.
 * Deterministic - same input + seed always gives same output.
 * Use seed (e.g., facility CCN or index) to vary output for same city.
 */
export function anonymizeCity(realCity: string | null | undefined, seed?: string | number): string {
  if (!realCity) return realCity || '';
  const seedStr = seed !== undefined ? String(seed) : '';
  const idx = hashToIndex(realCity + seedStr, FAKE_CITIES.length);
  return FAKE_CITIES[idx];
}

/**
 * Convert a real state to a fake one.
 * Deterministic - same input + seed always gives same output.
 * Use seed (e.g., facility CCN or index) to vary output for same state.
 */
export function anonymizeState(realState: string | null | undefined, seed?: string | number): string {
  if (!realState) return realState || '';
  const seedStr = seed !== undefined ? String(seed) : '';
  const idx = hashToIndex(realState + seedStr, FAKE_STATES.length);
  return FAKE_STATES[idx];
}

/**
 * Convert a real federal district to a fake one that matches the given fake state.
 * Deterministic - same input + seed always gives same output.
 * @param realDistrict - The real district value (used for hashing)
 * @param fakeState - The anonymized state to match the district to
 * @param seed - Optional seed for additional variation
 */
export function anonymizeDistrict(realDistrict: string | null | undefined, fakeState?: string, seed?: string | number): string {
  if (!realDistrict) return realDistrict || '';

  // Get districts for the fake state, or use a default
  const districts = fakeState && STATE_DISTRICT_MAP[fakeState]
    ? STATE_DISTRICT_MAP[fakeState]
    : STATE_DISTRICT_MAP["CA"]; // fallback to CA districts

  const seedStr = seed !== undefined ? String(seed) : '';
  const idx = hashToIndex(realDistrict + seedStr, districts.length);
  return districts[idx];
}
